import {
  useCancelRentAmendment,
  useRefreshRentAmendment
} from '@services/amendments/mutations/useRentAmendmentActions'
import { useRentAmendmentDocument } from '@services/amendments/mutations/useRentAmendmentPdf'
import { useRentAmendments } from '@services/amendments/queries/useRentAmendments'
import {
  previousPrices,
  PRICE_FIELDS,
  type Prices,
  type RentAmendment,
  type RentAmendmentStatus
} from '@services/amendments/types'
import { useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import {
  AlertTriangle,
  CalendarClock,
  Check,
  FileSignature,
  FileText,
  Megaphone,
  RefreshCw,
  ShieldCheck,
  X
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

import { money, round2 } from '../utils'

const STATUS_STYLE: Record<RentAmendmentStatus, string> = {
  SENT: 'bg-peach/15 text-peach',
  SIGNED: 'bg-primary/10 text-primary',
  APPLIED: 'bg-primary text-white',
  CANCELLED: 'bg-ink/[0.06] text-muted-foreground'
}

const sum = (prices: Prices) =>
  round2(PRICE_FIELDS.reduce((acc, f) => acc + (prices[f] || 0), 0))

// i18next HTML-escapes interpolated values by default ("10/2026" became
// "10&#x2F;2026"); React escapes the rendered text anyway.
const raw = { interpolation: { escapeValue: false } }

function Diff({ from, to }: { from: number; to: number }) {
  const delta = round2(to - from)
  return (
    <span className="flex items-baseline justify-end gap-1.5 tabular-nums">
      <span className="text-muted-foreground line-through decoration-ink/20">
        {money(from)}
      </span>
      <span className="font-semibold text-ink">{money(to)}</span>
      {delta !== 0 && (
        <span
          className={cn(
            'text-xs font-semibold',
            delta > 0 ? 'text-primary' : 'text-peach'
          )}
        >
          {delta > 0 ? '+' : ''}
          {money(delta)}
        </span>
      )}
    </span>
  )
}

function AmendmentRow({ amendment }: { amendment: RentAmendment }) {
  const { t } = useTranslation('amendments')
  const { confirm, dialog } = useConfirm()
  const cancel = useCancelRentAmendment()
  const refresh = useRefreshRentAmendment()
  const document = useRentAmendmentDocument()

  const before = previousPrices(amendment)
  const changed = PRICE_FIELDS.filter(
    (f) => round2(before[f] || 0) !== round2(amendment[f] || 0)
  )
  const cancelled = amendment.status === 'CANCELLED'
  const isNotice = amendment.kind === 'NOTICE'
  const isDeposit = amendment.kind === 'DEPOSIT'
  // A notice is binding once sent, but the admin can take it back until the
  // amounts are applied; an amendment only while it awaits signatures.
  const cancellable =
    amendment.status === 'SENT' || (isNotice && amendment.status === 'SIGNED')
  const statusLabel =
    isNotice && amendment.status === 'SIGNED'
      ? t('statuses.NOTIFIED')
      : t(`statuses.${amendment.status}`)

  const onCancel = async () => {
    const ok = await confirm({
      title: t(
        isNotice ? 'notice_cancel_confirm_title' : 'cancel_confirm_title'
      ),
      description: t(
        isNotice
          ? 'notice_cancel_confirm_description'
          : 'cancel_confirm_description'
      ),
      confirmText: t('cancel_amendment'),
      cancelText: t('cancel'),
      destructive: true
    })
    if (ok) cancel.mutate(amendment.id)
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-ink/[0.08] bg-white',
        cancelled && 'opacity-60'
      )}
    >
      {dialog}

      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            {isNotice ? (
              <Megaphone className="h-4 w-4" />
            ) : isDeposit ? (
              <ShieldCheck className="h-4 w-4" />
            ) : (
              <FileSignature className="h-4 w-4" />
            )}
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold text-ink">
              {amendment.amendment_number}
            </div>
            <div className="text-xs text-muted-foreground">
              {t(`kinds.${amendment.kind}`)}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" />
              {isDeposit
                ? t('effective_on_signing')
                : t('from_month', {
                    month: format(
                      parseISO(amendment.effective_from),
                      'MM/yyyy'
                    ),
                    ...raw
                  })}
            </div>
          </div>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold',
            STATUS_STYLE[amendment.status]
          )}
        >
          {statusLabel}
        </span>
      </div>

      <div className="mx-4 space-y-1.5 rounded-xl bg-ink/[0.025] px-3 py-2.5 text-sm">
        {isDeposit ? (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground">{t('deposit')}</span>
            <Diff
              from={amendment.previous_deposit ?? 0}
              to={amendment.deposit ?? 0}
            />
          </div>
        ) : (
          <>
            {changed.map((field) => (
              <div
                key={field}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="text-muted-foreground">
                  {t(`fields.${field}`)}
                </span>
                <Diff from={before[field] || 0} to={amendment[field] || 0} />
              </div>
            ))}
            <div className="flex items-baseline justify-between gap-3 border-t border-ink/[0.06] pt-1.5">
              <span className="font-semibold text-ink">{t('total')}</span>
              <Diff from={sum(before)} to={sum(amendment)} />
            </div>
          </>
        )}
      </div>

      {amendment.status === 'SENT' && amendment.signers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3">
          <span className="text-xs text-muted-foreground">{t('signers')}</span>
          {amendment.signers.map((signer) => {
            const signed = signer.status === 'completed'
            return (
              <span
                key={signer.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                  signed
                    ? 'bg-primary/10 text-primary'
                    : 'bg-ink/[0.06] text-muted-foreground'
                )}
              >
                {signed && <Check className="h-3 w-3" />}
                {signer.name}
              </span>
            )
          })}
        </div>
      )}

      {amendment.status === 'SIGNED' && (
        <p className="m-0 px-4 pt-3 text-xs text-muted-foreground">
          {t(isNotice ? 'notice_waiting' : 'signed_waiting')}
        </p>
      )}

      {amendment.skipped_paid_rents > 0 && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl bg-peach/15 px-3 py-2 text-xs text-ink">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-peach" />
          {isDeposit
            ? t('deposit_already_paid')
            : t('skipped_paid_rents', {
                count: amendment.skipped_paid_rents
              })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-ink/[0.06] px-4 py-2">
        <span className="truncate text-xs text-muted-foreground">
          {format(parseISO(amendment.created_at), 'dd.MM.yyyy HH:mm')}
          {amendment.created_by && ` · ${amendment.created_by}`}
        </span>
        {!cancelled && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => document.mutate(amendment.id)}
              disabled={document.isPending}
            >
              <FileText />
              {t('document')}
            </Button>
            {amendment.status === 'SENT' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => refresh.mutate(amendment.id)}
                disabled={refresh.isPending}
              >
                <RefreshCw
                  className={cn(refresh.isPending && 'animate-spin')}
                />
                {t('refresh')}
              </Button>
            )}
            {cancellable && (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={onCancel}
                disabled={cancel.isPending}
              >
                <X />
                {t('cancel_amendment')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Amendments of an offer (tenant) or a flat (landlord), newest first. */
export function RentAmendmentHistory(props: {
  offerId?: string
  flatId?: string
  showTitle?: boolean
}) {
  const { offerId, flatId, showTitle = true } = props
  const { t } = useTranslation('amendments')
  const queryClient = useQueryClient()
  const { data } = useRentAmendments({ offer: offerId, flat: flatId })

  // The webhook signs and applies amendments in the background. Whenever the
  // list changes, refresh what an amendment touches: the signed PDF lands in
  // the contract's "Dodatky k zmluve" and the amounts in offer and rents.
  const signature = data?.map((a) => `${a.id}:${a.status}`).join(',')
  useEffect(() => {
    if (!signature) return
    queryClient.invalidateQueries({ queryKey: ['contracts'] })
    queryClient.invalidateQueries({ queryKey: ['offers-detail'] })
    queryClient.invalidateQueries({ queryKey: ['offers'] })
    // Flat detail carries the landlord amounts and the flat's rents.
    queryClient.invalidateQueries({ queryKey: ['flats'] })
    queryClient.invalidateQueries({ queryKey: ['rents'] })
  }, [signature, queryClient])

  if (!data?.length) return null

  return (
    <div className="w-full space-y-2">
      {showTitle && (
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('history')}
        </div>
      )}
      <div className="space-y-2">
        {data.map((amendment) => (
          <AmendmentRow key={amendment.id} amendment={amendment} />
        ))}
      </div>
    </div>
  )
}
